import { getAuctioneerDirectoryUrl, getAuctioneerLogoUrl, getSellerUrl } from '@/utils/urls';
import { injectIntl, IntlShape } from 'react-intl';
import React, { Component } from 'react';
import { escapeQuotes } from '@/utils/escapeQuotes';
import isEqual from 'react-fast-compare';
import PageHeaderMeta from '@liveauctioneers/caterwaul-components/lib/PageHeaderMeta/PageHeaderMeta';
import { sanitizeTextForMetaTags } from '@/utils/safelyRender';

type Props = {
    baseUrl: string;
    canonicalURL: string;
    firstReview: string;
    intl: IntlShape;
    ratings: any;
    seller: any;
};

class RatingsPageHeaderMeta extends Component<Props> {
    shouldComponentUpdate = (nextProps: Props) => !isEqual(this.props, nextProps);

    getReviewData = (review) => {
        const { bidderFirstName, overall, review: reviewText, reviewHeadline, reviewSubmitted } = review;

        const description = `${reviewHeadline} ${reviewText}`;

        const safeDescription = sanitizeTextForMetaTags(escapeQuotes(description));
        return `{
            "@context": "http://schema.org",
            "@type": "Review",
            "datePublished": "${reviewSubmitted}",
            "description": "${safeDescription}",
            "author": {
                "@type": "Thing",
                "name": "${bidderFirstName}"
            },
            "reviewRating": {
                "@type": "Rating",
                "ratingValue": "${overall.toFixed(1)}"
            }
        }`;
    };

    render() {
        const { baseUrl, canonicalURL, firstReview, intl, ratings, seller } = this.props;
        const { overall, reviews, totalReviews } = ratings;
        const { address, address2, city, country, countryCode, logoId, name, sellerId, stateCode, zip } = seller;
        const { formatMessage, formatNumber } = intl;
        const displayAddress = address2 ? `${address} ${address2}` : `${address}`;
        const safeDisplayAddress = sanitizeTextForMetaTags(displayAddress);
        const reviewsCount = formatNumber(totalReviews);
        const houseLocation = stateCode || countryCode;
        const reviewSnippet = firstReview?.length > 158 ? `${firstReview.substring(0, 158)}...` : firstReview;

        const sellerUrl = getSellerUrl(sellerId, name);
        const directoryUrl = getAuctioneerDirectoryUrl();

        const logoUrl = getAuctioneerLogoUrl(logoId);

        if (!sellerId || !name) {
            return null;
        }

        const title = formatMessage(
            {
                id: 'review.meta.title',
            },
            {
                houseLocation,
                houseName: name,
                reviewsCount,
            }
        );
        const description = formatMessage(
            {
                id: 'review.meta.description',
            },
            {
                houseLocation,
                houseName: name,
                reviewsCount,
                reviewSnippet,
            }
        );

        const safeDescription = sanitizeTextForMetaTags(escapeQuotes(description));

        const reviewsData = Boolean(reviews?.length)
            ? reviews
                  .map((review) => {
                      return this.getReviewData(review);
                  })
                  .join(',\n')
            : '';

        return (
            <PageHeaderMeta canonical={canonicalURL} description={safeDescription} title={title}>
                <script type="application/ld+json">
                    {`{
                        "@context": "http://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [{
                                "@type": "ListItem",
                                "item": {
                                    "@id":"${baseUrl}",
                                    "name":"Home"
                                },
                                "position": 1
                            },
                            {
                                "@type": "ListItem",
                                "item": {
                                    "@id":"${directoryUrl}",
                                    "name":"Auction House Directory"
                                },
                                "position": 2
                        },
                        {
                            "@type": "ListItem",
                            "item": {
                                "@id": "${sellerUrl}",
                                "name":"${name}"
                            },
                            "position": 3
                        },{
                            "@type": "ListItem",
                            "item": {
                                "@id": "${canonicalURL}",
                                "name":"Reviews"
                            },
                            "position": 4
                        }]
                    }`}
                </script>

                <script type="application/ld+json">
                    {`{
                        "@context": "http://schema.org",
                        "@type": "LocalBusiness",
                        "address": {
                          "@type": "PostalAddress",
                          "addressCountry": "${country}",
                          "addressLocality": "${city}",
                          "addressRegion": "${stateCode}",
                          "streetAddress": "${escapeQuotes(safeDisplayAddress)}",
                          "postalCode": "${zip}"
                        },
                        "description": "${safeDescription}",
                        "name": "${name}",
                        "image": "${logoUrl}",
                        "aggregateRating": {
                            "@type": "AggregateRating",
                            "ratingValue": "${overall}",
                            "reviewCount": "${totalReviews}"
                          },
                        "review": [${reviewsData}],
                        "location": {
                            "@type": "PostalAddress",
                            "addressCountry": "${country}",
                            "addressLocality": "${city}",
                            "addressRegion": "${stateCode}",
                            "streetAddress": "${escapeQuotes(safeDisplayAddress)}",
                            "postalCode": "${zip}"
                          },
                        "logo": "${logoUrl}",
                        "url": "${sellerUrl}"
                    }`}
                </script>
            </PageHeaderMeta>
        );
    }
}

export default injectIntl(RatingsPageHeaderMeta);
